import { IconButtonProps, Popover, SxProps, Theme, Tooltip } from "@mui/material";
import { Box } from "@mui/system";
import { useCallback, useEffect, useState } from "react";

import { FeatureName } from "@/common/constants/featureName";
import { ROUTE_PATH } from "@/common/constants/routing";
import { ChatExtraContext } from "@/common/helpers/entity/chat";
import { useEffectOnceIf } from "@/common/hooks/effect/useEffectOnceIf";
import { useAppSelector, useAppThunkDispatch } from "@/common/hooks/redux";
import { useAppHistory } from "@/common/hooks/routing/useAppHistory";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { chatHubService } from "@/common/realtime/chatHubService";
import { ValidationHelper } from "@/common/validation";
import { featureManager } from "@/config/features";
import { apiClient } from "@/core/api/ApiClient";
import {
  ChatType,
  GeneralScopeDto,
  GeneralScopeRequestDto,
  GeneralScopeType,
} from "@/core/api/generated/v1";
import * as chatActivitySlice from "@/store/communication/chatActivityOverviewSlice";
import * as chatsSlice from "@/store/communication/chatsSlice";
import {
  CommentChatParams,
  OpenChatInfo,
  OpenChatPlacement,
} from "@/store/communication/chatsSlice";
import { selectChat, selectIsChatOpening } from "@/store/communication/selectors";

import AppBadge from "../../Badge/AppBadge";
import AppIconButton from "../../Button/AppIconButton";
import AppIcon from "../../Icons/AppIcon";
import Chat from "./Chat";

export interface OwnProps {
  chatType: ChatType;
  chatId?: string | null;
  chatScope?: GeneralScopeDto | null;
  extraContext?: ChatExtraContext;
  chatPlacement?: OpenChatPlacement | "custom";
  commentChatParams?: CommentChatParams;
  /** Link to custom page. */
  to?: string;
  /** Whether dot is shown when new activity has been performed in the chat. */
  withActivityIndicator?: boolean;
  withAutoOpenOnMount?: boolean;
  tooltipTitle?: string;
  disabled?: boolean;
  activatorButtonProps?: IconButtonProps;
  sx?: SxProps<Theme>;
}

interface Props extends OwnProps {}

/** Displays button/icon that opens chat. */
export default function ChatActivator({
  chatType,
  chatId,
  chatScope,
  extraContext,
  chatPlacement,
  commentChatParams,
  to,
  withActivityIndicator,
  withAutoOpenOnMount,
  tooltipTitle = "Open chat",
  disabled,
  activatorButtonProps,
  sx,
}: Props) {
  chatPlacement ||= "popover";

  const history = useAppHistory();
  const thunkDispatch = useAppThunkDispatch();
  const { enqueueSnackbar } = useAppSnackbar();

  const [chatIdLocal, setChatIdLocal] = useState<string | undefined | null>(undefined);
  const [openedChat, setOpenedChat] = useState<OpenChatInfo | undefined>(undefined);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isPopoverOpen = Boolean(anchorEl);

  const chat = useAppSelector((state) => selectChat(state, { chatId: chatId, scope: chatScope }));
  const resolvedChatId = chatId || chat?.id || chatIdLocal;
  const chatActivity = useAppSelector(
    (x) => x.communication.chatActivityOverviews.chatActivityMap[resolvedChatId || ""] || undefined,
  );
  const isChatOpening = useAppSelector((state) => selectIsChatOpening(state, resolvedChatId));

  const shouldTrackActivity =
    featureManager.isEnabled(FeatureName.ChatActivity) && withActivityIndicator;
  const hasNewActivity = (withActivityIndicator && chatActivity?.hasNewActivity) || false;

  useEffect(() => {
    (async () => {
      if (shouldTrackActivity) {
        if (chatId) {
          thunkDispatch(chatActivitySlice.queueGetChatActivityOverview(chatId));
        } else if (chatScope) {
          const scopeRequest: GeneralScopeRequestDto = {
            identifier: chatScope.identifier || undefined,
            subIdentifier: chatScope.subIdentifier || undefined,
            type: chatScope.type || GeneralScopeType.Dynamic,
          };
          const chatByScope = await apiClient.chatsApi.apiV1ChatsByScopeGet({
            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
            identifier: scopeRequest.identifier || undefined,
            subIdentifier: scopeRequest.subIdentifier || undefined,
            type: scopeRequest.type || undefined,
          });
          const chatIdByScope = chatByScope.data.id!;
          setChatIdLocal(chatIdByScope);
          thunkDispatch(chatActivitySlice.queueGetChatActivityOverview(chatIdByScope));
        }
      }
    })();
  }, [shouldTrackActivity, chatId, chatScope]);

  // explicitly subscribe on specific chat activity updates
  useEffect(() => {
    if (shouldTrackActivity) {
      chatHubService.subscribeToChatActivityUpdates({
        chatId: resolvedChatId!,
      });
    }

    return () => {
      if (shouldTrackActivity) {
        chatHubService.unsubscribeFromChatActivityUpdates({
          chatId: resolvedChatId!,
        });
      }
    };
  }, [resolvedChatId]);

  const openChatInPopover = async (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setAnchorEl(e?.currentTarget || null);
    try {
      const newOpenedChat = await thunkDispatch(
        chatsSlice.openChat({
          placement: "popover",
          chatType,
          chatId,
          scope: chatScope,
          commentChatParams,
        }),
      );
      setOpenedChat(newOpenedChat);
    } catch (err) {
      const newValidation = ValidationHelper.handleApiErrorResponse(err);
      enqueueSnackbar(newValidation.generalError || "Failed to open the chat.", {
        variant: "error",
      });
      setAnchorEl(null);
    }
  };

  const openChatInSideStack = async () => {
    try {
      const newOpenedChat = await thunkDispatch(
        chatsSlice.openChat({
          placement: "stack",
          chatType,
          chatId,
          scope: chatScope,
          commentChatParams,
        }),
      );
      setOpenedChat(newOpenedChat);
    } catch (err) {
      const newValidation = ValidationHelper.handleApiErrorResponse(err);
      enqueueSnackbar(newValidation.generalError, { variant: "error" });
    }
  };

  const openChatInPage = useCallback(async () => {
    if (chatId) {
      history.push(ROUTE_PATH.CHAT_VIEW(chatId, { chatType }));
    } else if (chatScope) {
      history.push(ROUTE_PATH.CHAT_VIEW_BY_SCOPE(chatScope, { chatType }));
    }
  }, [chatScope, history]);

  const handleActivatorClick = (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e?.stopPropagation();
    if (to) {
      history.push(to);
      return;
    }

    if (chatPlacement === "popover") {
      openChatInPopover(e);
    } else if (chatPlacement === "stack") {
      openChatInSideStack();
    } else if (chatPlacement === "page") {
      openChatInPage();
    }
  };

  const handlePinChat = useCallback(() => {
    if (chatPlacement !== "stack") {
      openChatInSideStack();
      setAnchorEl(null);
    }
  }, [chatPlacement, openChatInSideStack, setAnchorEl]);

  const handleChatPopoverClose = useCallback(() => {
    setAnchorEl(null);
    thunkDispatch(chatsSlice.closeChat({ openedChat, chat }));
  }, [openedChat, chat, setAnchorEl]);

  useEffectOnceIf(!!withAutoOpenOnMount, () => {
    handleActivatorClick();
  });

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <AppIconButton
          {...activatorButtonProps}
          disabled={disabled}
          loading={isChatOpening}
          onClick={handleActivatorClick}
          sx={sx}
        >
          <AppBadge
            color='primary'
            variant='dot'
            sx={{
              "& .MuiBadge-badge": {
                opacity: hasNewActivity ? 1 : 0,
              },
            }}
          >
            <AppIcon of='chat' />
          </AppBadge>
        </AppIconButton>
      </Tooltip>

      {/* Chat popover */}
      <Popover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={handleChatPopoverClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box
          sx={{
            minWidth: 400,
            maxWidth: { sm: "90vw", md: 400, lg: 500 },
            width: { md: 400, lg: 500 },
          }}
        >
          <Chat
            chatType={chatType}
            chatId={chatId}
            scope={chatScope}
            extraContext={extraContext}
            fullWidth
            allowPin
            allowClose
            allowLeave
            size='small'
            displayProps={{
              contentHeader: false,
              contentFolding: false,
            }}
            onPin={handlePinChat}
            onClose={handleChatPopoverClose}
          />
        </Box>
      </Popover>
    </>
  );
}
